import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ExportTools = ({ onExport, availableFormats, includeIntegrityChecks }) => {
  const { t } = useTranslation();
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeAuditTrail, setIncludeAuditTrail] = useState(true);
  const [compressionLevel, setCompressionLevel] = useState('standard');
  const [encryptionEnabled, setEncryptionEnabled] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const formatOptions = availableFormats?.map(format => ({
    value: format,
    label: format?.toUpperCase()
  })) || [
    { value: 'pdf', label: 'PDF' },
    { value: 'csv', label: 'CSV' },
    { value: 'xlsx', label: 'Excel' },
    { value: 'json', label: 'JSON' }
  ];

  const compressionOptions = [
    { value: 'none', label: 'No Compression' },
    { value: 'standard', label: 'Standard Compression' },
    { value: 'high', label: 'High Compression' }
  ];

  const handleExportPackage = async () => {
    setIsGenerating(true);
    
    try {
      const exportConfig = {
        format: selectedFormat,
        includeMetadata,
        includeAuditTrail,
        compressionLevel,
        encryptionEnabled,
        includeIntegrityChecks,
        timestamp: new Date()?.toISOString(),
        auditorId: 'auditor@clearequal.com',
        packageId: `audit-package-${Date.now()}`
      };

      // Simulate package generation
      setTimeout(() => {
        console.log('Generating audit package:', exportConfig);
        onExport?.(exportConfig, selectedFormat);
        setIsGenerating(false);
        
        // Show success message
        alert(`Audit evidence package generated successfully in ${selectedFormat?.toUpperCase()} format with integrity verification.`);
      }, 3000);
      
    } catch (error) {
      setIsGenerating(false);
      console.error('Export failed:', error);
    }
  };

  const getEstimatedSize = () => {
    let baseSize = 25; // Base size in MB
    
    if (includeMetadata) baseSize += 5;
    if (includeAuditTrail) baseSize += 15;
    if (includeIntegrityChecks) baseSize += 10;
    
    switch (compressionLevel) {
      case 'none': return `${Math.round(baseSize)} MB`;
      case 'standard': return `${Math.round(baseSize * 0.6)} MB`;
      case 'high': return `${Math.round(baseSize * 0.4)} MB`;
      default: return `${Math.round(baseSize * 0.6)} MB`;
    }
  };

  const getFormatDescription = (format) => {
    switch (format) {
      case 'pdf':
        return 'Formatted reports with embedded metadata and digital signatures';
      case 'csv':
        return 'Raw data export suitable for analysis tools and databases';
      case 'xlsx':
        return 'Structured spreadsheet with multiple sheets and formatting';
      case 'json':
        return 'Machine-readable format with complete data hierarchy';
      default:
        return 'Standard export format';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-6 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
            <Icon name="Package" size={20} className="text-green-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Audit Evidence Export</h3>
            <p className="text-sm text-muted-foreground">
              Generate tamper-evident audit packages with integrity verification
            </p>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-6">
        {/* Export Format */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Export Format
          </label>
          <Select
            value={selectedFormat}
            onChange={(e) => setSelectedFormat(e?.target?.value)}
            options={formatOptions}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground mt-2">
            {getFormatDescription(selectedFormat)}
          </p>
        </div>

        {/* Export Options */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground">Export Options</h4>
          
          <div className="space-y-3">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={includeMetadata}
                onChange={(e) => setIncludeMetadata(e?.target?.checked)}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
              />
              <div>
                <span className="text-sm font-medium text-foreground">Include Metadata</span>
                <p className="text-xs text-muted-foreground">
                  Document properties, creation dates, and verification details
                </p>
              </div>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={includeAuditTrail}
                onChange={(e) => setIncludeAuditTrail(e?.target?.checked)}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
              />
              <div>
                <span className="text-sm font-medium text-foreground">Include Audit Trail</span>
                <p className="text-xs text-muted-foreground">
                  Complete chronological log of all system activities and access records
                </p>
              </div>
            </label>

            {includeIntegrityChecks && (
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked
                  disabled
                  className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary opacity-50"
                />
                <div>
                  <span className="text-sm font-medium text-foreground">Integrity Verification</span>
                  <p className="text-xs text-muted-foreground">
                    SHA-256 checksums and digital signatures (always included)
                  </p>
                </div>
              </label>
            )}

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={encryptionEnabled}
                onChange={(e) => setEncryptionEnabled(e?.target?.checked)}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
              />
              <div>
                <span className="text-sm font-medium text-foreground">Enable Encryption</span>
                <p className="text-xs text-muted-foreground">
                  AES-256 encryption with secure key distribution
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Compression Level */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Compression Level
          </label>
          <Select
            value={compressionLevel}
            onChange={(e) => setCompressionLevel(e?.target?.value)}
            options={compressionOptions}
            className="w-full"
          />
        </div>

        {/* Package Information */}
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-foreground mb-3">Package Information</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Estimated Size:</span>
              <span className="ml-2 font-medium text-foreground">{getEstimatedSize()}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Generation Time:</span>
              <span className="ml-2 font-medium text-foreground">~2-5 minutes</span>
            </div>
            <div>
              <span className="text-muted-foreground">Package ID:</span>
              <span className="ml-2 font-mono text-xs text-foreground">audit-pkg-{Date.now()}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Retention:</span>
              <span className="ml-2 font-medium text-foreground">90 days</span>
            </div>
          </div>
        </div>

        {/* Export Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-xs text-muted-foreground">
            Package will include immutable timestamps and digital signatures
          </div>
          
          <Button
            onClick={handleExportPackage}
            disabled={isGenerating}
            iconName={isGenerating ? "Loader2" : "Download"}
            iconPosition="left"
            className={isGenerating ? "animate-spin" : ""}
          >
            {isGenerating ? 'Generating Package...' : 'Generate Package'}
          </Button>
        </div>

        {/* Security Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-yellow-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-yellow-800 mb-1">Security Notice</p>
              <p className="text-yellow-700">
                Exported packages contain sensitive compliance data. Ensure secure handling, 
                transmission, and storage according to your organization's data protection policies. 
                All exports are logged and monitored for audit purposes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportTools;